import * as THREE from 'three'
import { BLUE_NOISE_SIZE, blueNoiseData } from './blue-noise'

/**
 * Blue Noise 纹理工具
 *
 * 提供 32x32 Blue Noise 纹理用于 Dithering 抗锯齿
 *
 * 特点：
 * - 懒加载：仅在首次调用时创建纹理
 * - 渲染器级缓存：使用 WeakMap 为每个渲染器维护独立实例
 * - 单例模式：无渲染器参数时返回全局共享实例
 *
 * 参考 PlayCanvas: src/scene/graphics/noise-textures.js
 */

/** 全局纹理缓存（无渲染器时使用） */
let globalTexture: THREE.DataTexture | null = null

/** 渲染器级纹理缓存（每个渲染器一个实例） */
const textureCache = new WeakMap<THREE.WebGLRenderer, THREE.DataTexture>()

/**
 * 创建 Blue Noise 纹理
 * @internal
 */
export function createBlueNoiseTexture(): THREE.DataTexture {
  const data = blueNoiseData()

  const texture = new THREE.DataTexture(
    data,
    BLUE_NOISE_SIZE,
    BLUE_NOISE_SIZE,
    THREE.RGBAFormat,
    THREE.UnsignedByteType
  )

  // 纹理配置（与 PlayCanvas 一致）
  texture.name = 'BlueNoise32'
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true

  return texture
}

/**
 * 获取 Blue Noise 纹理
 *
 * @param renderer - 可选的 WebGL 渲染器，提供时使用渲染器级缓存
 * @returns Blue Noise DataTexture 实例
 *
 * @example
 * ```typescript
 * // 全局共享实例（推荐用于单渲染器场景）
 * const texture = getBlueNoiseTexture()
 *
 * // 渲染器级缓存（推荐用于多渲染器场景）
 * const texture = getBlueNoiseTexture(renderer)
 * ```
 */
export function getBlueNoiseTexture(renderer?: THREE.WebGLRenderer): THREE.DataTexture {
  // 如果提供了渲染器，使用渲染器级缓存
  if (renderer) {
    let texture = textureCache.get(renderer)
    if (!texture) {
      texture = createBlueNoiseTexture()
      textureCache.set(renderer, texture)
    }
    return texture
  }

  // 否则使用全局单例
  if (!globalTexture) {
    globalTexture = createBlueNoiseTexture()
  }
  return globalTexture
}

/**
 * 释放 Blue Noise 纹理资源
 *
 * @param renderer - 可选的渲染器，提供时仅释放该渲染器的缓存
 *
 * @example
 * ```typescript
 * // 释放全局实例
 * disposeBlueNoiseTexture()
 *
 * // 释放特定渲染器的实例
 * disposeBlueNoiseTexture(renderer)
 * ```
 */
export function disposeBlueNoiseTexture(renderer?: THREE.WebGLRenderer): void {
  if (renderer) {
    const texture = textureCache.get(renderer)
    if (texture) {
      texture.dispose()
      textureCache.delete(renderer)
    }
  } else if (globalTexture) {
    globalTexture.dispose()
    globalTexture = null
  }
}
